import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, Spinner } from '@/components/admin/ui';
import {
  ChartCard,
  DeviceLink,
  EmptyState,
  KpiTile,
} from '@/components';
import type { BehaviorPulseResponse, BehaviorSession } from '@/lib/admin-types';
import { DOW_LABELS, kindMeta } from '@/lib/behavior-viz';
import { PRIMARY_HEX } from '@/lib/colors';
import { formatAdminTime, formatDurationSeconds, formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

function SwarmDot({
  session,
  index,
  total,
  motionOn,
}: {
  session: BehaviorSession;
  index: number;
  total: number;
  motionOn: boolean;
}) {
  const meta = kindMeta(session.primary_kind);
  const angle = (index / Math.max(total, 1)) * Math.PI * 2;
  const radius = 28 + (index % 5) * 18;
  const x = 50 + Math.cos(angle) * (radius / 1.8);
  const y = 50 + Math.sin(angle) * (radius / 2.4);
  const size = Math.min(56, 22 + Math.sqrt(session.event_count) * 6);

  return (
    <motion.div
      initial={motionOn ? { opacity: 0, scale: 0.6 } : false}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: Math.min(index * 0.03, 0.4), type: 'spring', stiffness: 320, damping: 24 }}
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${x}%`, top: `${y}%` }}
      title={`${session.nickname ?? session.installation_id.slice(0, 8)} · ${session.summary}`}
    >
      <div
        className={clsx(
          'flex items-center justify-center rounded-lg text-[10px] font-semibold text-white shadow-md ring-2 ring-white/80',
          meta.bar
        )}
        style={{ width: size, height: size }}
      >
        {session.event_count}
      </div>
    </motion.div>
  );
}

function ActiveSessionRow({ session }: { session: BehaviorSession }) {
  const meta = kindMeta(session.primary_kind);
  return (
    <li className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-nova-accent">
      <span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full', meta.bar)} />
      <div className="min-w-0 flex-1">
        <DeviceLink
          installationId={session.installation_id}
          nickname={session.nickname}
          className="text-sm font-medium"
        />
        <p className="truncate text-xs text-nova-muted-foreground">{session.summary}</p>
      </div>
      <div className="shrink-0 text-right text-[11px] tabular-nums text-nova-muted-foreground">
        <p>{formatDurationSeconds(session.duration_seconds)}</p>
        <p>{formatAdminTime(session.ended_at)}</p>
      </div>
    </li>
  );
}

function PulsePage() {
  const reduceMotion = useReducedMotion();
  const motionOn = !reduceMotion;

  const q = useQuery({
    queryKey: ['admin', 'behavior', 'pulse'],
    queryFn: () =>
      apiJson<BehaviorPulseResponse>('/v1/admin/analytics/behavior/pulse'),
    refetchInterval: 10_000,
  });

  const hourChart = useMemo(
    () =>
      (q.data?.hour_buckets ?? []).map((b) => ({
        hour: `${String(b.hour).padStart(2, '0')}:00`,
        sessions: b.sessions,
        events: b.events,
      })),
    [q.data?.hour_buckets]
  );

  const heatMax = useMemo(() => {
    let m = 1;
    for (const c of q.data?.heatmap ?? []) m = Math.max(m, c.sessions);
    return m;
  }, [q.data?.heatmap]);

  const heatGrid = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of q.data?.heatmap ?? []) {
      map.set(`${c.dow}-${c.hour}`, c.sessions);
    }
    return DOW_LABELS.map((label, dow) => ({
      label,
      cells: Array.from({ length: 24 }, (_, hour) => ({
        hour,
        count: map.get(`${dow}-${hour}`) ?? 0,
      })),
    }));
  }, [q.data?.heatmap]);

  if (q.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (q.isError) {
    return (
      <p className="text-sm text-red-600">
        {(q.error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void q.refetch()}>
          Retry
        </button>
      </p>
    );
  }

  const data = q.data!;
  const kpis = data.kpis;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-nova-foreground">Live pulse</h2>
        <p className="mt-0.5 text-sm text-nova-muted-foreground">
          Active sessions (last {data.active_window_minutes}m), 24h rhythm, and a 7‑day
          heatmap. Auto-refreshes every 10s.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile label="Active now" value={kpis.active_now} />
        <KpiTile label="Sessions (24h)" value={kpis.sessions_24h} />
        <KpiTile label="Events (24h)" value={kpis.events_24h} />
        <KpiTile label="Devices (24h)" value={kpis.distinct_devices_24h} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="overflow-hidden">
          <div className="mb-3 flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-nova-foreground">Session swarm</h3>
            <p className="text-xs text-nova-muted-foreground">
              {formatInt(data.active_sessions.length)} active · size = event count
            </p>
          </div>
          {data.active_sessions.length === 0 ? (
            <EmptyState title="No one active right now" />
          ) : (
            <div className="relative mx-auto aspect-[16/10] max-h-72 w-full overflow-hidden rounded-lg bg-[radial-gradient(ellipse_at_center,_#e8f4f4_0%,_#f8fafc_55%,_#eef2ff_100%)]">
              {data.active_sessions.map((s, i) => (
                <SwarmDot
                  key={s.id}
                  session={s}
                  index={i}
                  total={data.active_sessions.length}
                  motionOn={motionOn}
                />
              ))}
            </div>
          )}
          {data.active_sessions.length > 0 && (
            <ul className="mt-4 max-h-56 space-y-0.5 overflow-y-auto border-t border-nova-border pt-3">
              {data.active_sessions.map((s) => (
                <ActiveSessionRow key={s.id} session={s} />
              ))}
            </ul>
          )}
        </Card>

        <ChartCard
          title="Last 24 hours by Pacific hour"
          tooltip="Session starts and events (excluding flashcard views) in each Pacific hour bucket."
          height={288}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={hourChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="sessions" name="Sessions" fill={PRIMARY_HEX} radius={[4, 4, 0, 0]} />
              <Bar dataKey="events" name="Events" fill="#94a3b8" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Card>
        <div className="mb-3 flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-nova-foreground">7‑day session heatmap</h3>
          <p className="text-xs text-nova-muted-foreground">Pacific weekday × hour · darker = more sessions</p>
        </div>
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <div className="mb-1 flex gap-0.5 pl-10">
              {Array.from({ length: 24 }, (_, h) => (
                <div
                  key={h}
                  className="w-4 text-center text-[9px] tabular-nums text-nova-muted-foreground"
                >
                  {h % 3 === 0 ? h : ''}
                </div>
              ))}
            </div>
            <div className="space-y-0.5">
              {heatGrid.map((row) => (
                <div key={row.label} className="flex items-center gap-0.5">
                  <div className="w-10 shrink-0 text-[11px] font-medium text-nova-muted-foreground">
                    {row.label}
                  </div>
                  {row.cells.map((cell) => {
                    const intensity = cell.count === 0 ? 0 : 0.15 + (cell.count / heatMax) * 0.85;
                    return (
                      <div
                        key={`${row.label}-${cell.hour}`}
                        title={`${row.label} ${cell.hour}:00 — ${cell.count} session${cell.count === 1 ? '' : 's'}`}
                        className="h-4 w-4 rounded-sm"
                        style={{
                          backgroundColor:
                            cell.count === 0
                              ? '#f3f4f6'
                              : `rgba(15, 138, 138, ${intensity.toFixed(3)})`,
                        }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/admin/analytics/pulse')({
  component: PulsePage,
});
